import { http } from './http';
import { ROUTES } from './config';
import type {
  AdjustInventoryInput,
  Category,
  CategoryInput,
  HeroSlide,
  HeroSlideInput,
  InventoryAdjustResult,
  InventoryHistoryPage,
  Product,
  ProductCampaign,
  ProductCampaignInput,
  ProductInput,
  ProductListFilters,
  ProductListPage,
  ProductUpdateInput,
  PromoTile,
  PromoTileInput,
  SearchSuggestionsResult,
  BrandInfo,
} from '../types/ecommerce';

/**
 * ProductService client (catalog + categories + inventory). List/detail are anonymous
 * (public storefront browse, same "public reads, manager writes" split as bookingApi's
 * catalog); writes are manager+, enforced in-service via [RequireRoles] — the gateway lets
 * POST/PUT/DELETE/PATCH through to the controller rather than 401ing them at the edge.
 */
export const productsApi = {
  list(filters: ProductListFilters = {}): Promise<ProductListPage> {
    const params = new URLSearchParams();
    if (filters.categoryId) params.set('categoryId', filters.categoryId);
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    if (filters.featured !== undefined) params.set('featured', String(filters.featured));
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    const qs = params.toString();
    return http.get(`${ROUTES.products}${qs ? `?${qs}` : ''}`, { anonymous: true });
  },

  get(id: string): Promise<Product> {
    return http.get(`${ROUTES.products}/${id}`, { anonymous: true });
  },

  /** Distinct brands of published products with counts — GET /products/brands (anonymous). */
  brands(): Promise<BrandInfo[]> {
    return http.get(`${ROUTES.products}/brands`, { anonymous: true });
  },

  /** Header/typeahead suggestions — small top-N product + category matches. */
  search(q: string, limit = 8): Promise<SearchSuggestionsResult> {
    return http.get(`${ROUTES.products}/search?q=${encodeURIComponent(q)}&limit=${limit}`, { anonymous: true });
  },

  create(body: ProductInput): Promise<Product> {
    return http.post(ROUTES.products, body);
  },

  /** RowVersion must be the value last read via get() — stale value 409s (optimistic concurrency). */
  update(id: string, body: ProductUpdateInput): Promise<Product> {
    return http.put(`${ROUTES.products}/${id}`, body);
  },

  /** Quick enable/disable from a table row — changes ONLY the status (a full update would need the whole product). */
  setStatus(id: string, status: 'Draft' | 'Published' | 'Archived'): Promise<{ status: string }> {
    return http.patch(`${ROUTES.products}/${id}/status`, { status });
  },

  remove(id: string): Promise<void> {
    return http.delete(`${ROUTES.products}/${id}`);
  },

  adjustInventory(id: string, body: AdjustInventoryInput): Promise<InventoryAdjustResult> {
    return http.patch(`${ROUTES.products}/${id}/inventory`, body);
  },

  /** Append-only stock ledger for one product, newest first, cursor-paginated. */
  getInventoryHistory(id: string, cursor?: string, pageSize = 20): Promise<InventoryHistoryPage> {
    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (cursor) params.set('cursor', cursor);
    return http.get(`${ROUTES.products}/${id}/inventory/history?${params.toString()}`);
  },

  /** `includeInactive`: admin management page passes true to see disabled categories too
   * (to re-enable them); the public storefront default (false) hides them from navigation. */
  listCategories(parentId?: string, includeInactive = false): Promise<Category[]> {
    const params = new URLSearchParams();
    if (parentId) params.set('parentId', parentId);
    if (includeInactive) params.set('includeInactive', 'true');
    const qs = params.toString();
    return http.get(`${ROUTES.categories}${qs ? `?${qs}` : ''}`, { anonymous: true });
  },

  getCategory(id: string): Promise<Category> {
    return http.get(`${ROUTES.categories}/${id}`, { anonymous: true });
  },

  createCategory(body: CategoryInput): Promise<Category> {
    return http.post(ROUTES.categories, body);
  },

  toggleCategory(id: string): Promise<{ id: string; isActive: boolean }> {
    return http.put(`${ROUTES.categories}/${id}/toggle`, {});
  },

  /** Sends every category's id in its new display order — SortOrder becomes its index. */
  reorderCategories(orderedIds: string[]): Promise<void> {
    return http.put(`${ROUTES.categories}/reorder`, { orderedIds });
  },

  updateCategory(id: string, body: CategoryInput): Promise<void> {
    return http.put(`${ROUTES.categories}/${id}`, body);
  },

  removeCategory(id: string): Promise<void> {
    return http.delete(`${ROUTES.categories}/${id}`);
  },

  /** Homepage hero carousel. `activeOnly` defaults true storefront-side; admin passes false
   * to manage the full set including drafts. */
  listHeroSlides(activeOnly = true): Promise<HeroSlide[]> {
    return http.get(`${ROUTES.heroSlides}?activeOnly=${activeOnly}`, { anonymous: true });
  },

  createHeroSlide(body: HeroSlideInput): Promise<HeroSlide> {
    return http.post(ROUTES.heroSlides, body);
  },

  updateHeroSlide(id: string, body: HeroSlideInput): Promise<void> {
    return http.put(`${ROUTES.heroSlides}/${id}`, body);
  },

  removeHeroSlide(id: string): Promise<void> {
    return http.delete(`${ROUTES.heroSlides}/${id}`);
  },

  /** Homepage promo-tile grid. `activeOnly` defaults true storefront-side; admin passes
   * false to manage the full set including drafts. */
  listPromoTiles(activeOnly = true): Promise<PromoTile[]> {
    return http.get(`${ROUTES.promoTiles}?activeOnly=${activeOnly}`, { anonymous: true });
  },

  createPromoTile(body: PromoTileInput): Promise<PromoTile> {
    return http.post(ROUTES.promoTiles, body);
  },

  updatePromoTile(id: string, body: PromoTileInput): Promise<void> {
    return http.put(`${ROUTES.promoTiles}/${id}`, body);
  },

  removePromoTile(id: string): Promise<void> {
    return http.delete(`${ROUTES.promoTiles}/${id}`);
  },

  /** Homepage campaign carousel (limited sales / festive offers). `runningOnly` defaults true
   * storefront-side; admin passes false to manage scheduled/expired/paused campaigns too. */
  listCampaigns(runningOnly = true): Promise<ProductCampaign[]> {
    return http.get(`${ROUTES.productCampaigns}?runningOnly=${runningOnly}`, { anonymous: true });
  },

  getCampaign(id: string): Promise<ProductCampaign> {
    return http.get(`${ROUTES.productCampaigns}/${id}`, { anonymous: true });
  },

  /** Every currently-running, homepage-enabled campaign, ordered for direct rendering —
   * the ecommerce homepage groups these by `section` client-side. */
  getHomepageCampaigns(): Promise<ProductCampaign[]> {
    return http.get(`${ROUTES.productCampaigns}/homepage`, { anonymous: true });
  },

  createCampaign(body: ProductCampaignInput): Promise<ProductCampaign> {
    return http.post(ROUTES.productCampaigns, body);
  },

  updateCampaign(id: string, body: ProductCampaignInput): Promise<void> {
    return http.put(`${ROUTES.productCampaigns}/${id}`, body);
  },

  removeCampaign(id: string): Promise<void> {
    return http.delete(`${ROUTES.productCampaigns}/${id}`);
  },
};
